import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { gameCategories, articles } from '@/mocks/mahjongData';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const game = gameCategories.find((g) => g.slug === slug);

  if (!game) {
    return <NotFound />;
  }

  const relatedArticles = articles.filter(
    (a) =>
      a.title.includes(game.name) ||
      a.slug.includes(slug || '') ||
      a.content.includes(game.name)
  );

  return (
    <>
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: game.name },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          <img
            src={game.imageUrl}
            alt={game.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-black/20"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
              <h1 className="font-heading text-3xl md:text-4xl text-white mb-3">{game.name}</h1>
              <p className="text-white/80 text-base md:text-lg max-w-2xl">{game.shortDesc}</p>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto py-10">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Main Content */}
            <article className="flex-1">
              <h2 className="font-heading text-xl text-foreground-900 mb-4">玩法介绍</h2>
              <p className="text-foreground-600 leading-relaxed mb-8">{game.description}</p>

              <h2 className="font-heading text-xl text-foreground-900 mb-4">游戏规则</h2>
              <ul className="space-y-2 mb-8">
                {game.rules.map((rule, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-foreground-600">
                    <span className="w-6 h-6 flex items-center justify-center bg-primary-100 text-primary-600 rounded-full text-xs font-medium flex-shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    {rule}
                  </li>
                ))}
              </ul>

              <h2 className="font-heading text-xl text-foreground-900 mb-4">玩法特点</h2>
              <div className="flex flex-wrap gap-3 mb-8">
                {game.features.map((f) => (
                  <span key={f} className="px-4 py-2 bg-secondary-100 text-secondary-700 rounded-full text-sm font-medium">
                    {f}
                  </span>
                ))}
              </div>

              <div className="mt-8 p-6 bg-primary-50 border border-primary-200 rounded-lg text-center">
                <h3 className="font-heading text-lg text-primary-700 mb-2">准备好开始了吗？</h3>
                <p className="text-sm text-foreground-600 mb-4">
                  无需下载安装，打开浏览器即可立即体验{game.name}
                </p>
                <Link
                  to={`/category/${game.slug}`}
                  className="inline-block px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  立即开局
                </Link>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:w-[320px] flex-shrink-0">
              <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-5 mb-6">
                <h3 className="font-heading text-base text-foreground-900 mb-4">其他玩法</h3>
                <div className="space-y-2">
                  {gameCategories
                    .filter((g) => g.slug !== game.slug)
                    .map((g) => (
                      <Link
                        key={g.slug}
                        to={`/category/${g.slug}`}
                        className="flex items-center gap-3 p-3 rounded-md hover:bg-background-50 transition-colors cursor-pointer group"
                      >
                        <span className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-600 rounded-md text-xs font-medium flex-shrink-0">
                          {g.name.charAt(0)}
                        </span>
                        <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors font-medium">
                          {g.name}
                        </span>
                      </Link>
                    ))}
                </div>
              </div>

              {relatedArticles.length > 0 && (
                <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-5">
                  <h3 className="font-heading text-base text-foreground-900 mb-4">相关文章</h3>
                  <div className="space-y-3">
                    {relatedArticles.slice(0, 3).map((article) => (
                      <Link
                        key={article.slug}
                        to={`/article/${article.slug}`}
                        className="block p-3 rounded-md hover:bg-background-50 transition-colors cursor-pointer"
                      >
                        <span className="text-xs text-accent-600">{article.category}</span>
                        <p className="text-sm text-foreground-700 font-medium mt-1 line-clamp-2 hover:text-primary-600 transition-colors">
                          {article.title}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>

        {/* More Games */}
        <section className="py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <h2 className="font-heading text-2xl text-foreground-900 text-center mb-10">探索更多玩法</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {gameCategories
                .filter((g) => g.slug !== game.slug)
                .slice(0, 3)
                .map((g) => (
                  <Link
                    key={g.slug}
                    to={`/category/${g.slug}`}
                    className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={g.imageUrl}
                        alt={g.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-base text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {g.name}
                      </h3>
                      <p className="text-sm text-foreground-500 mt-1 line-clamp-2">{g.shortDesc}</p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${game.name} - 麻将电脑版在线玩`,
            description: game.shortDesc,
            url: `https://mahjong-online.com/category/${game.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '麻将电脑版在线玩',
              url: 'https://mahjong-online.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://mahjong-online.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: game.name,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}